import { HookStyleCarousel } from '../components/HookStyleCarousel';
import { MusicPreview } from '../components/MusicPreview';
import { SubtitlePositionControl } from '../components/SubtitlePositionControl';
import { AudioControls } from '../components/AudioControls';
import { MusicVolumeControl } from '../components/MusicVolumeControl';
import { TransitionControls } from '../components/TransitionControls';
/** Новый монтаж: материалы, предпросмотр, настройки по слоям и запуск. */
import { useEffect, useRef, useState } from 'react';
import { formatNumber, ls, t } from '../i18n';
import type { ReactElement, ReactNode } from 'react';
import {
  HIGHLIGHT_COLOR_INFO,
  HOOK_ACCENTS,
  SUBTITLE_BOXES,
  SUBTITLE_BOX_LABELS,
  SUBTITLE_SIZES,
  SUBTITLE_SIZE_LABELS,
  HOOK_ACCENT_LABELS,
  PLAYBACK_SPEEDS,
  type PlaybackSpeed,
  MOODS,
  SUBTITLE_FONT_CHOICES,
  SUBTITLE_FONT_INFO,
  SUBTITLE_PRESET_CHOICES,
  SUBTITLE_LABELS,
} from '@reels/shared/constants';
import type { HighlightColor, Mood, ProjectSettings, SubtitlePreset } from '@reels/shared';
import { ColorControls } from '../components/ColorControls';
import { Chips, Segmented, Switch } from '../components/Controls';
import { LaunchButton } from '../components/LaunchButton';
import { Materials, useMaterials } from '../components/Materials';
import { OfflineBanner } from '../components/States';
import { SubtitlePreview } from '../components/SubtitlePreview';
import { highlightHex, previewFontFamily } from '../lib/subtitles';
import {
  HIGHLIGHT_ORDER,
  UPCOMING_HIGHLIGHTS,
  type UpcomingHighlight,
} from '../lib/editor-upcoming';
import { addDemoSources } from '../lib/demo-sources';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';

const editingModeOptions = () => [
  { value: 'full', label: t('editor.mode.full') },
  { value: 'summary', label: t('editor.mode.summary') },
] as const;
const durationOptions = () => [
  { value: 'auto', label: t('editor.duration.auto') },
  { value: '30', label: t('editor.duration.s', { n: 30 }) },
  { value: '60', label: t('editor.duration.s', { n: 60 }) },
  { value: '90', label: t('editor.duration.s', { n: 90 }) },
] as const;

const moodLabel = (mood: Mood): string => t(`editor.mood.${mood}`);

const framingOptions = () => [
  { value: 'auto', label: t('editor.framing.auto') },
  { value: 'center', label: t('editor.framing.center') },
  { value: 'blur', label: t('editor.framing.blur') },
] as const;

const captionLanguageOptions = () => [
  { value: 'auto', label: t('editor.captionLang.auto') },
  { value: 'ru', label: t('editor.captionLang.ru') },
  { value: 'en', label: t('editor.captionLang.en') },
] as const;

const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Превью во всю ширину уменьшается при прокрутке: после 40px начинает,
 * к 300px становится 228px по центру. Само превью не перекладываем, а
 * масштабируем: Safari на iOS не пересчитывает при смене ширины высоту
 * рамки с aspect-ratio и кегль в cqw. Обёртке задаём высоту уменьшенного
 * превью, чтобы контент ниже поднимался вслед.
 */
function useShrinkOnScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const node = ref.current;
    const preview = node?.firstElementChild as HTMLElement | null;
    if (!node || !preview) return;
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const share = Math.min(1, Math.max(0, (window.scrollY - 40) / 260));
      const full = preview.offsetWidth;
      const scale = full > 228 ? 1 - (1 - 228 / full) * share : 1;
      preview.style.transform = scale < 1 ? `scale(${scale.toFixed(4)})` : '';
      node.style.height = scale < 1 ? `${preview.offsetHeight * scale}px` : '';
    };
    const schedule = (): void => { if (!frame) frame = requestAnimationFrame(update); };
    // Ширина экрана меняется при повороте, высота превью — пока грузятся шрифты.
    const observer = new ResizeObserver(schedule);
    observer.observe(preview);
    window.addEventListener('scroll', schedule, { passive: true });
    update();
    return () => { observer.disconnect(); window.removeEventListener('scroll', schedule); cancelAnimationFrame(frame); };
  }, []);
  return ref;
}

/** Строка параметра: подпись слева, управление справа или под ней. */
function Param({
  title,
  hint,
  stacked,
  children,
}: {
  title: string;
  hint?: string;
  stacked?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={stacked ? 'param stacked' : 'param'}>
      <div className="col grow">
        <span className="param-title">{title}</span>
        {hint ? <span className="small muted">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

export function Editor(): ReactElement {
  const { state, dispatch } = useStore();
  const actions = useActions();
  const settings = state.settings;
  const [preparingDemo, setPreparingDemo] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const started = !!state.project && state.project.status !== 'draft';
  const previewRef = useShrinkOnScroll<HTMLDivElement>();
  // Новое из макета, чего ещё нет в рендере: только в интерфейсе, на сервер не уходит.
  const [upcoming, setUpcoming] = useState<{
    highlight: UpcomingHighlight | null;
  }>({ highlight: null });

  // При возвращении в редактор обновляем баланс и статистику.
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void actions.loadProfile();
  }, [actions, state.profile]);

  // Превью показывает и выбранные в интерфейсе шрифт и цвет, которых ещё нет в рендере.
  const previewFont = previewFontFamily(settings.font);
  const previewHighlight = UPCOMING_HIGHLIGHTS.find(item => item.value === upcoming.highlight)?.hex ?? highlightHex(settings.highlight);

  const update = (patch: Partial<ProjectSettings>): void => {
    dispatch({ type: 'settings/patch', patch });
  };

  const materials = useMaterials(preparingDemo);

  const billing = state.profile?.billing;
  const exhausted = !!billing && billing.available === 0;
  const subtitleValue: SubtitlePreset | 'off' = settings.subtitles;
  // Цвет подсветки есть только у пресетов, которые подсвечивают текущее слово.
  const highlights = subtitleValue === 'karaoke' || subtitleValue === 'bold' || subtitleValue === 'editorial';
  const addDemo = (): void => {
    setPreparingDemo(true);
    void addDemoSources(actions.addFiles).catch(() => actions.toast(t('editor.demoFailed')))
      .finally(() => setPreparingDemo(false));
  };
  // Без материалов запуск ведёт наверх, к раскрытой карточке загрузки.
  const showMaterials = (): void => {
    window.scrollTo({ top: 0, behavior: reducedMotion() ? 'instant' : 'smooth' });
    setMaterialsOpen(true);
  };
  const trial = !!billing?.balances.trial && billing.available === billing.balances.trial;

  return (
    <>
      <h1>{t('editor.title')}</h1>
      {!state.online ? <OfflineBanner /> : null}
      <Materials materials={materials} open={materialsOpen} onToggle={() => setMaterialsOpen(value => !value)}
        disabled={started} onDemo={addDemo} />
      <div className="editor-preview" ref={previewRef}>
        <div className="preview">
          <SubtitlePreview animated={false} preset={subtitleValue} position={settings.subtitlePosition}
            colorFilter={settings.colorFilter} colorIntensity={settings.colorIntensity}
            hookEnabled={settings.hook} hookStyle={settings.hookStyle} hookAccent={settings.hookAccent} subtitleBox={settings.subtitleBox} subtitleSize={settings.subtitleSize} hookText={t('editor.hookSample')}
            fontFamily={previewFont} highlight={previewHighlight}
            hookPosition={settings.hookPosition}
            onPositionChange={started || typeof settings.subtitlePosition !== 'number' ? undefined : subtitlePosition => update({ subtitlePosition })}
            onHookPositionChange={started || typeof settings.subtitlePosition !== 'number' ? undefined : hookPosition => update({ hookPosition })} />
        </div>
      </div>
      <fieldset className="editor-settings" disabled={started}>
      <section className="editor-section subtitles-section" aria-labelledby="text-title">
        <h2 id="text-title" className="section-title">{t('editor.section.text')}</h2>
        <div className="subtitle-chips">
      <Chips
        ariaLabel={t('editor.subtitles.style')}
        overlay
        options={(['off', ...SUBTITLE_PRESET_CHOICES] as const).map((value) => ({
          value,
          label: ls(SUBTITLE_LABELS[value]),
        }))}
        value={subtitleValue}
        onChange={(value) => update({ subtitles: value })}
      />

      {highlights ? (
        <Chips
          ariaLabel={t('editor.subtitles.highlight')}
          overlay
          className="swatches"
          options={HIGHLIGHT_ORDER.map((key) => {
            const extra = UPCOMING_HIGHLIGHTS.find(item => item.value === key);
            return extra
              ? { value: key, label: ls(extra.label), swatch: extra.hex }
              : { value: key, label: ls(HIGHLIGHT_COLOR_INFO[key as HighlightColor].label), swatch: HIGHLIGHT_COLOR_INFO[key as HighlightColor].hex };
          })}
          value={upcoming.highlight ?? settings.highlight}
          onChange={(highlight) => {
            const extra = UPCOMING_HIGHLIGHTS.find(item => item.value === highlight);
            setUpcoming(prev => ({ ...prev, highlight: extra?.value ?? null }));
            if (!extra) update({ highlight: highlight as HighlightColor });
          }}
        />
      ) : null}
      {subtitleValue !== 'off' ? (
        <Chips
          ariaLabel={t('editor.subtitles.font')}
          overlay
          className="font-chips"
          options={SUBTITLE_FONT_CHOICES.map((value) => ({ value, label: ls(SUBTITLE_FONT_INFO[value].label), fontFamily: SUBTITLE_FONT_INFO[value].family }))}
          value={settings.font}
          onChange={(font) => update({ font })}
        />
      ) : null}
        </div>
        {subtitleValue !== 'off' ? <div className="card params compact">
          <SubtitlePositionControl value={settings.subtitlePosition} onChange={subtitlePosition => update({ subtitlePosition })} />
          <Param title={t('editor.subtitles.size')} stacked>
            <Segmented ariaLabel={t('editor.subtitles.size')} options={SUBTITLE_SIZES.map(value => ({ value, label: ls(SUBTITLE_SIZE_LABELS[value]) }))}
              value={settings.subtitleSize} onChange={subtitleSize => update({ subtitleSize })} />
          </Param>
          <Param title={t('editor.subtitles.box')} stacked>
            <Segmented ariaLabel={t('editor.subtitles.boxAria')} options={SUBTITLE_BOXES.map(value => ({ value, label: ls(SUBTITLE_BOX_LABELS[value]) }))}
              value={settings.subtitleBox} onChange={subtitleBox => update({ subtitleBox })} />
          </Param>
          <Param title={t('editor.subtitles.sound')} hint={t('editor.subtitles.soundHint')}>
            <Switch checked={settings.subtitleSound} onChange={subtitleSound => update({ subtitleSound })} label={t('editor.subtitles.sound')} />
          </Param>
        </div> : null}
        <div className="card params compact hook-params">
          <Param title={t('editor.hook.title')} hint={t('editor.hook.hint')}>
            <Switch checked={settings.hook} onChange={(hook) => update({ hook })} label={t('editor.hook.title')} />
          </Param>
          {settings.hook ? <>
            <Param title={t('editor.hook.accent')} stacked>
              <Segmented ariaLabel={t('editor.hook.accent')} options={HOOK_ACCENTS.map(value => ({ value, label: ls(HOOK_ACCENT_LABELS[value]) }))}
                value={settings.hookAccent} onChange={hookAccent => update({ hookAccent })} />
            </Param>
            <Param title={t('editor.hook.style')} stacked>
              <HookStyleCarousel value={settings.hookStyle} onChange={hookStyle => update({ hookStyle })}
                accent={previewHighlight} accentMode={settings.hookAccent} fontFamily={previewFont} box={settings.subtitleBox === 'none' ? false : settings.subtitleBox} />
            </Param>
          </> : null}
        </div>
        <div className="caption-language">
          <div className="col">
            <span className="caption-language-title">{t('editor.caption.title')}</span>
            <span className="small muted">{t('editor.caption.hint')}</span>
          </div>
          <Segmented
            ariaLabel={t('editor.caption.aria')}
            options={captionLanguageOptions()}
            value={settings.captionLanguage}
            onChange={(captionLanguage) => update({ captionLanguage })}
          />
        </div>
      </section>
      <section className="editor-section" aria-labelledby="picture-title">
        <h2 id="picture-title" className="section-title">{t('editor.section.picture')}</h2>
        <span className="param-label">{t('editor.color.title')}</span>
        <ColorControls showPreview preset={settings.colorFilter} intensity={settings.colorIntensity} onChange={update} />
        <div className="card params">
        <Param title={t('editor.framing.title')} hint={t('editor.framing.hint')} stacked>
          <Segmented
            ariaLabel={t('editor.framing.aria')}
            options={framingOptions()}
            value={settings.framing}
            onChange={(framing) => update({ framing })}
          />
        </Param>
        <Param title={t('editor.zooms.title')} hint={t('editor.zooms.hint')}>
          <Switch checked={settings.accentZooms} onChange={(accentZooms) => update({ accentZooms })} label={t('editor.zooms.title')} />
        </Param>

          <Param title={t('editor.transitions')} stacked>
            <TransitionControls type={settings.transition} duration={settings.transitionDuration} onChange={update} />
          </Param>
        </div>
      </section>
      <section className="editor-section" aria-labelledby="montage-title">
        <h2 id="montage-title" className="section-title">{t('editor.section.montage')}</h2>
        <div className="card params">
        <Param title={t('editor.montage.mode')} hint={settings.editingMode === 'summary'
          ? t('editor.montage.summaryHint')
          : t('editor.montage.fullHint')} stacked>
          <Segmented ariaLabel={t('editor.montage.mode')} options={editingModeOptions()}
            value={settings.editingMode} onChange={(editingMode) => update({ editingMode })} />
        </Param>
        {settings.editingMode === 'summary' ? (
          <Param title={t('editor.montage.summaryLength')} hint={t('editor.montage.summaryLengthHint')} stacked>
            <Segmented ariaLabel={t('editor.montage.summaryLength')} options={durationOptions()}
              value={settings.duration} onChange={(duration) => update({ duration })} />
          </Param>
        ) : null}
        <Param title={t('editor.montage.pauses')} hint={t('editor.montage.pausesHint')}>
          <Switch
            checked={settings.cleanPauses}
            onChange={(cleanPauses) => update({ cleanPauses })}
            label={t('editor.montage.pauses')}
          />
        </Param>
        <Param title={t('editor.montage.speed')} hint={t('editor.montage.speedHint')} stacked>
          <Segmented ariaLabel={t('editor.montage.speed')}
            options={PLAYBACK_SPEEDS.map(speed => ({ value: String(speed), label: formatNumber(speed) + 'x' }))}
            value={String(settings.playbackSpeed)}
            onChange={value => update({ playbackSpeed: Number(value) as PlaybackSpeed })} />
        </Param>

        </div>
      </section>
      <section className="editor-section" aria-labelledby="sound-title">
        <h2 id="sound-title" className="section-title">{t('editor.section.sound')}</h2>
        <div className="card params">
        <Param title={t('editor.sound.mood')} stacked>
          <Segmented ariaLabel={t('editor.sound.mood')}
            options={[{ value: 'off', label: t('editor.mood.off') }, ...MOODS.map(mood => ({ value: mood, label: moodLabel(mood) }))]}
            value={settings.music === 'auto' ? 'calm' : settings.music}
            onChange={(music) => update({ music })} />
        </Param>
        {settings.music !== 'off' ? <Param title={t('editor.sound.volume')} stacked>
          <MusicVolumeControl value={settings.musicVolume} onChange={musicVolume => update({ musicVolume })} />
        </Param> : null}
        </div>
        {settings.music !== 'off' ? <MusicPreview mood={settings.music === 'auto' ? 'calm' : settings.music} /> : null}
        <div className="card params">
        <Param title={t('editor.sound.noise')} stacked>
          <AudioControls mode={settings.noiseReduction} onChange={noiseReduction => update({ noiseReduction })} />
        </Param>
        </div>
      </section>
      </fieldset>
      {!started && exhausted ? <section className="card editor-paywall" aria-label={t('editor.paywall.aria')}>
        <strong>{billing.reserved > 0 ? t('editor.paywall.reserved') : t('editor.paywall.empty')}</strong>
        {billing.subscription ? <>
          <p>{billing.addon.available
            ? t('editor.paywall.addon', { n: billing.addon.videos, price: billing.addon.tributeAvailable ? `${formatNumber(billing.addon.rub)} ₽` : `${formatNumber(billing.addon.stars)} ⭐` })
            : t('editor.paywall.addonOff')}</p>
        </> : <>
          <p>{t('editor.paywall.subscribe')}</p>
          <div className="editor-paywall-prices">
            {billing.packages.map(pack => <div key={pack.id}>
              <span>{t('editor.paywall.pack', { title: ls(pack.title), n: pack.videos })}</span>
              <strong>{billing.starsEnabled ? `${formatNumber(pack.stars)} ⭐` : `${formatNumber(pack.rub)} ₽`}</strong>
            </div>)}
          </div>
        </>}
        <button className="btn block" onClick={() => dispatch({ type: 'route', screen: billing.subscription ? 'limit' : 'balance' })}>
          {billing.subscription ? billing.addon.available ? t('editor.paywall.buyMore') : t('editor.paywall.balance') : t('editor.paywall.choose')}
        </button>
      </section> : null}
      {started && state.project?.status !== 'failed' ? <p className="small muted center" role="status">
        {state.project?.status === 'done' ? t('editor.status.done') : t('editor.status.working')}
      </p> : null}
      <LaunchButton
        hidden={started && state.project?.status !== 'failed'}
        retry={state.project?.status === 'failed'}
        stage={!materials.mainRows.length ? 'empty' : materials.uploading ? 'uploading' : materials.ready ? 'ready' : 'check'}
        percent={materials.reactionUploading ? materials.videoPercent : materials.illustrationPercent}
        loading={state.busy}
        caption={exhausted ? [t('editor.caption.exhausted1'), t('editor.caption.exhausted2')]
          : [trial ? t('editor.caption.trial') : t('editor.caption.clean'), billing ? t('editor.caption.spend', { n: billing.available }) : null]}
        onShowMaterials={showMaterials}
        onLaunch={() => {
          if (!state.online) { actions.toast(t('editor.offline')); return; }
          void actions.startGeneration();
        }}
      />
    </>
  );
}
